import { useTranslation } from 'react-i18next'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CardDashboard, IllustratedMessage, Timeline } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import { ValidationRequestTimelineProps } from './ValidationRequestTimeline.types'
import { useValidationRequestTimeline } from './useValidationRequestTimeline'

export const validationRequestLogsQueryKey = (id: string) => ['/validation-requests', id, '/logs']

export function ValidationRequestTimeline({ id }: Readonly<ValidationRequestTimelineProps>) {
  const { t } = useTranslation()
  const { adminService } = useServices()

  const {
    data: logs,
    isLoading,
    error,
    errorMessage,
    refetch
  } = useQuery(validationRequestLogsQueryKey(id), adminService.getValidationRequestLogs, { id })

  const timeline = useValidationRequestTimeline(logs)

  if (isLoading) return null

  if (error) {
    return (
      <CardDashboard data-testid="validation-request-timeline-error">
        <IllustratedMessage
          illustration="general-error"
          title={t('typography.validationRequestTimeline.loadError')}
          description={errorMessage}
          cta={{
            label: t('button.tryAgain'),
            onClick: () => {
              // A failed retry shows through the query's error state.
              refetch().catch(() => undefined)
            }
          }}
        />
      </CardDashboard>
    )
  }

  if (!timeline) {
    return (
      <CardDashboard data-testid="validation-request-empty-timeline">
        <IllustratedMessage illustration="no-results" description={t('typography.noActivity')} />
      </CardDashboard>
    )
  }

  return (
    <CardDashboard title={t('typography.allActivity')} data-testid="validation-request-timeline">
      <Timeline timeline={timeline} />
    </CardDashboard>
  )
}
